import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import Input from '../../components/common/Input';
import Button from '../../components/common/Button';
import Loader from '../../components/common/Loader';

function LoginPage() {
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [validationErrors, setValidationErrors] = useState({});
  const [isFormValid, setIsFormValid] = useState(false);
  const { login, isLoading } = useAuth();
  const navigate = useNavigate();

  // Validation functions
  const validateEmail = (email) => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  };

  const validateUsername = (username) => {
    const usernameRegex = /^[a-zA-Z0-9_]{3,20}$/;
    return usernameRegex.test(username);
  };

  const validateField = (name, value) => {
    let error = '';
    
    switch (name) {
      case 'username':
        if (!value) {
          error = 'Username or email is required';
        } else if (value.length < 3) {
          error = 'Must be at least 3 characters';
        } else if (!validateEmail(value) && !validateUsername(value)) {
          error = 'Enter a valid username or email address';
        }
        break;
        
      case 'password':
        if (!value) {
          error = 'Password is required';
        } else if (value.length < 6) {
          error = 'Password must be at least 6 characters';
        }
        break;
        
      default:
        break;
    }
    
    return error;
  };

  // Validation message component
  const ValidationMessage = ({ error, type = 'error' }) => {
    if (!error) return null;
    
    const styles = {
      error: 'bg-gradient-to-r from-red-50 to-pink-50 border-red-200 text-red-700',
      warning: 'bg-gradient-to-r from-amber-50 to-orange-50 border-amber-200 text-amber-700',
      success: 'bg-gradient-to-r from-green-50 to-emerald-50 border-green-200 text-green-700'
    };
    
    const icons = {
      error: '❌',
      warning: '⚠️',
      success: '✅'
    };
    
    return (
      <div className={`${styles[type]} border px-3 py-2 rounded-lg text-sm font-medium mt-1 shadow-sm animate-fade-in`}>
        <div className="flex items-center">
          <span className="mr-2">{icons[type]}</span>
          {error}
        </div>
      </div>
    );
  };

  const handleInputChange = (field, value) => {
    if (field === 'username') {
      setUsername(value);
    } else if (field === 'password') {
      setPassword(value);
    }
    
    // Real-time validation
    const error = validateField(field, value);
    setValidationErrors(prev => ({
      ...prev,
      [field]: error
    }));
    
    // Check if form is valid
    const newErrors = { ...validationErrors, [field]: error };
    const hasErrors = Object.values(newErrors).some(err => err !== '');
    const hasValues = username.trim() !== '' && password.trim() !== '';
    setIsFormValid(!hasErrors && hasValues);
    
    setError(''); // Clear general errors when typing
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    // Validate all fields before submission
    const usernameError = validateField('username', username);
    const passwordError = validateField('password', password);
    
    if (usernameError || passwordError) {
      setValidationErrors({
        username: usernameError,
        password: passwordError
      });
      return;
    }

    if (!username || !password) {
      setError('Please enter both username/email and password.');
      return;
    }

    const result = await login(username, password);

    if (result.success) {
      navigate('/dashboard');
    } else {
      setError(result.error || 'Login failed. Please check your credentials.');
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-gray-50 via-blue-50 to-indigo-100 p-4">
      {isLoading && (
        <div className="fixed inset-0 bg-gradient-to-br from-gray-900/80 to-indigo-900/80 backdrop-blur-sm flex items-center justify-center z-50">
          <div className="bg-white rounded-xl p-6 shadow-2xl">
            <Loader />
          </div>
        </div>
      )}
      <div className="bg-white p-8 rounded-xl shadow-2xl w-full max-w-xl border border-gray-200 text-center relative hover:shadow-3xl transition-shadow duration-300">
        <div className="bg-gradient-to-r from-indigo-50 to-blue-50 rounded-xl p-6 border border-indigo-200 mb-6">
          <h2 className="text-2xl font-bold text-gray-900 mb-2 flex items-center justify-center">
            <div className="w-3 h-3 bg-indigo-500 rounded-full mr-3"></div>
            Admin Login
          </h2>
          <p className="text-gray-600 text-sm">Access your TourKingdom dashboard</p>
        </div>

        {error && (
          <div className="bg-gradient-to-r from-red-50 to-pink-50 border border-red-200 text-red-700 px-6 py-4 rounded-xl relative mb-6 shadow-lg" role="alert">
            <div className="flex items-center">
              <div className="w-2 h-2 bg-red-500 rounded-full mr-3"></div>
              <span className="block sm:inline font-medium text-sm">{error}</span>
            </div>
          </div>
        )}

        <div className="bg-gradient-to-r from-green-50 to-emerald-50 rounded-xl p-6 border border-green-200 mb-6">
          <form onSubmit={handleSubmit} className="space-y-5">
            <div>
              <Input
                label="Username / Email"
                id="username"
                type="text"
                value={username}
                onChange={(e) => handleInputChange('username', e.target.value)}
                placeholder="Enter your username or email"
                required
                aria-label="Username or Email"
                className={`${validationErrors.username ? 'border-red-300 focus:border-red-500 focus:ring-red-500' : 'border-green-200 focus:border-green-500 focus:ring-green-500'}`}
              />
              <ValidationMessage error={validationErrors.username} />
              {username && !validationErrors.username && (validateEmail(username) || validateUsername(username)) && (
                <ValidationMessage error="Valid format" type="success" />
              )}
            </div>
            
            <div>
              <Input
                label="Password"
                id="password"
                type="password"
                value={password}
                onChange={(e) => handleInputChange('password', e.target.value)}
                placeholder="Enter your password"
                required
                aria-label="Password"
                className={`${validationErrors.password ? 'border-red-300 focus:border-red-500 focus:ring-red-500' : 'border-green-200 focus:border-green-500 focus:ring-green-500'}`}
              />
              <ValidationMessage error={validationErrors.password} />
              {password && !validationErrors.password && password.length >= 6 && (
                <ValidationMessage error="Password looks good" type="success" />
              )}
            </div>

            <div className="pt-4">
              <Button
                type="submit"
                className={`w-full py-3 text-lg font-semibold bg-gradient-to-r from-indigo-500 to-blue-600 hover:from-indigo-600 hover:to-blue-700 text-white shadow-lg hover:shadow-xl transition-all duration-200 ${
                  (Object.values(validationErrors).some(err => err !== '') || !username || !password) 
                    ? 'opacity-50 cursor-not-allowed' 
                    : ''
                }`}
                disabled={isLoading || Object.values(validationErrors).some(err => err !== '') || !username || !password}
              >
                {isLoading ? 'Logging In...' : 'Login'}
              </Button>
            </div>
            
            {/* Login Tips */}
            <div className="bg-gradient-to-r from-blue-50 to-indigo-50 rounded-lg p-3 border border-blue-200 mt-4">
              <div className="flex items-start">
                <div className="w-2 h-2 bg-blue-500 rounded-full mr-3 mt-1.5"></div>
                <div className="text-xs text-blue-700">
                  <div className="font-medium mb-1">💡 Login Tips:</div>
                  <ul className="space-y-1 text-blue-600">
                    <li>• Use your username or email address</li>
                    <li>• Password is case-sensitive</li>
                    <li>• Contact admin if you forgot your credentials</li>
                  </ul>
                </div>
              </div>
            </div>
          </form>
        </div>

        <div className="bg-gradient-to-r from-gray-50 to-slate-50 rounded-xl p-4 border border-gray-200">
          <div className="text-xs text-gray-600 flex items-center justify-center">
            <div className="w-2 h-2 bg-gray-400 rounded-full mr-2"></div>
            &copy; {new Date().getFullYear()} TourKingdom. All rights reserved.
          </div>
        </div>
      </div>
    </div>
  );
}

export default LoginPage;